import { useEffect, useRef, useState } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import {
  Alert,
  Button,
  Empty,
  Input,
  Modal,
  Select,
  Spin,
  Table,
  Tag,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  Bold,
  Eye,
  Italic,
  Link2,
  List,
  ListOrdered,
  RefreshCw,
  Send,
} from "lucide-react";
import { DashboardLayout } from "admin/components/layout/components/DashboardLayout";
import { Navbar } from "admin/components/layout/components/Navbar";
import { END_POINT } from "config/environment";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { hasPermission } from "utils/hasPermission";

type RecipientType = "talent" | "recruiter" | "both";

interface EmailBroadcast {
  id: string;
  sender: string;
  recipient_type: RecipientType;
  subject: string;
  body?: string;
  recipient_count: number;
  sent_count: number;
  failed_count: number;
  status: string;
  sent_at: string;
  delivery_statuses?: Record<string, number>;
}

interface EmailTemplate {
  label: string;
  subject: string;
  body: string;
}

const templates: EmailTemplate[] = [
  {
    label: "Platform announcement",
    subject: "An update from Arno",
    body: "<p>We have an important update to share with you.</p><p>More details will follow soon.</p>",
  },
  {
    label: "Account update",
    subject: "An update about your Arno account",
    body: "<p>We are writing to let you know about an update to your account.</p>",
  },
  {
    label: "Onboarding",
    subject: "Welcome to Arno",
    body: "<p>Welcome to Arno. We are glad you are here.</p><p>Sign in to continue setting up your account.</p>",
  },
  {
    label: "Maintenance notification",
    subject: "Scheduled maintenance notice",
    body: "<p>We will be performing scheduled maintenance.</p><ul><li>When: [date and time]</li><li>Expected duration: [duration]</li></ul>",
  },
  {
    label: "Policy or terms update",
    subject: "An update to our policies",
    body: "<p>Our policies have been updated. Please review the latest version at your convenience.</p>",
  },
  {
    label: "General communication",
    subject: "A message from Arno",
    body: "<p>We would like to share the following information with you.</p>",
  },
];

const recipientLabels: Record<RecipientType, string> = {
  talent: "Talent",
  recruiter: "Recruiters",
  both: "Talent and Recruiters",
};

const statusColors: Record<string, string> = {
  queued: "blue",
  sending: "processing",
  sent: "green",
  partial: "orange",
  failed: "red",
};

const emailMessagesUrl = `${END_POINT.BASE_URL}/v1/administrator/email-messages`;

export const EmailMessaging = () => {
  const { token, userInfo } = useGetUserInfo();
  const editorRef = useRef<HTMLDivElement>(null);
  const [recipientType, setRecipientType] = useState<RecipientType | undefined>();
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [recipientCount, setRecipientCount] = useState<number | null>(null);
  const [countLoading, setCountLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState<EmailBroadcast[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [selectedEmail, setSelectedEmail] = useState<EmailBroadcast | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const canSend = hasPermission(userInfo, "Send Email Messages");

  const authConfig = {
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  const loadHistory = async (page = historyPage) => {
    setHistoryLoading(true);
    try {
      const response = await axios.get(emailMessagesUrl, {
        ...authConfig,
        params: { page, per_page: 10 },
      });
      setHistory(response.data.data ?? []);
      setHistoryPage(response.data.current_page ?? page);
      setHistoryTotal(response.data.total ?? 0);
    } catch {
      message.error("Email history could not be loaded. Please try again.");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (canSend) void loadHistory();
  }, [canSend, token]);

  useEffect(() => {
    if (!recipientType) {
      setRecipientCount(null);
      return;
    }

    let active = true;
    setCountLoading(true);
    axios
      .get(`${emailMessagesUrl}/recipient-count`, {
        ...authConfig,
        params: { recipient_type: recipientType },
      })
      .then((response) => {
        if (active) setRecipientCount(response.data.recipient_count);
      })
      .catch(() => {
        if (active) {
          setRecipientCount(null);
          message.error("Recipient count could not be loaded. Please try again.");
        }
      })
      .finally(() => {
        if (active) setCountLoading(false);
      });

    return () => {
      active = false;
    };
  }, [recipientType, token]);

  const applyTemplate = (templateLabel: string) => {
    const template = templates.find((item) => item.label === templateLabel);
    if (!template) return;
    setSubject(template.subject);
    setBody(template.body);
    if (editorRef.current) editorRef.current.innerHTML = template.body;
  };

  const formatSelection = (command: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false);
    if (editorRef.current) setBody(editorRef.current.innerHTML);
  };

  const addLink = () => {
    const candidate = linkUrl.trim();
    if (!/^https?:\/\//i.test(candidate) && !/^mailto:/i.test(candidate)) {
      message.error("Enter a valid https, http, or email link.");
      return;
    }
    editorRef.current?.focus();
    document.execCommand("createLink", false, candidate);
    if (editorRef.current) setBody(editorRef.current.innerHTML);
    setLinkOpen(false);
    setLinkUrl("");
  };

  const sendEmail = async () => {
    if (!recipientType || !subject.trim() || !body.trim() || !recipientCount) return;

    setSending(true);
    try {
      await axios.post(
        emailMessagesUrl,
        {
          recipient_type: recipientType,
          subject: subject.trim(),
          body,
          confirmed_recipient_count: recipientCount,
        },
        authConfig
      );
      message.success("Email queued for delivery.");
      setSubject("");
      setBody("");
      setRecipientType(undefined);
      setRecipientCount(null);
      if (editorRef.current) editorRef.current.innerHTML = "";
      await loadHistory(1);
    } catch (error: any) {
      if (error.response?.status === 409 && error.response.data.recipient_count !== undefined) {
        setRecipientCount(error.response.data.recipient_count);
      }
      message.error(
        error.response?.data?.message ||
          "The email could not be queued. Please try again."
      );
    } finally {
      setSending(false);
    }
  };

  const openEmailDetails = async (email: EmailBroadcast) => {
    try {
      const response = await axios.get(`${emailMessagesUrl}/${email.id}`, authConfig);
      setSelectedEmail(response.data);
    } catch {
      message.error("Email details could not be loaded. Please try again.");
    }
  };

  const confirmSend = () => {
    if (!recipientType || !subject.trim() || !body.trim() || !recipientCount) return;
    Modal.confirm({
      title: "Confirm bulk email",
      content: `This email will be sent to ${recipientCount.toLocaleString()} users. Do you want to continue?`,
      okText: "Send email",
      cancelText: "Review email",
      onOk: sendEmail,
    });
  };

  const columns: ColumnsType<EmailBroadcast> = [
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      render: (value: string, record) => (
        <button
          className="text-left font-medium text-slate-800 hover:text-teal-700"
          onClick={() => void openEmailDetails(record)}
        >
          {value}
        </button>
      ),
    },
    {
      title: "Recipients",
      dataIndex: "recipient_type",
      key: "recipient_type",
      render: (value: RecipientType) => recipientLabels[value],
    },
    {
      title: "Users",
      dataIndex: "recipient_count",
      key: "recipient_count",
      render: (value: number) => value.toLocaleString(),
    },
    {
      title: "Delivery",
      dataIndex: "status",
      key: "status",
      render: (value: string, record) => (
        <span className="flex flex-wrap items-center gap-2">
          <Tag color={statusColors[value] ?? "default"}>{value}</Tag>
          <span className="text-xs text-slate-500">
            {record.sent_count} sent / {record.failed_count} failed
          </span>
        </span>
      ),
    },
    {
      title: "Sent",
      dataIndex: "sent_at",
      key: "sent_at",
      render: (value: string) => new Date(value).toLocaleString(),
    },
    { title: "Sender", dataIndex: "sender", key: "sender" },
    {
      title: "",
      key: "details",
      align: "right",
      render: (_, record) => (
        <Button
          aria-label={`View ${record.subject}`}
          icon={<Eye size={16} />}
          onClick={() => void openEmailDetails(record)}
        />
      ),
    },
  ];

  if (!canSend) {
    return (
      <DashboardLayout>
        <Navbar title="Email Messaging" description="" />
        <Alert
          className="mt-6"
          type="error"
          showIcon
          message="You do not have permission to send email messages."
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Navbar
        title="Email Messaging"
        description="Send announcements and updates to Talent and Recruiters"
      />
      <div className="space-y-10 pb-12 pt-5">
        <section aria-labelledby="compose-heading">
          <div className="mb-5 flex items-center justify-between gap-3">
            <h2 id="compose-heading" className="text-lg font-semibold text-slate-900">
              Compose email
            </h2>
            <Select
              aria-label="Choose an email template"
              allowClear
              placeholder="Start from a template"
              className="w-64 max-w-full"
              options={templates.map((template) => ({
                label: template.label,
                value: template.label,
              }))}
              onChange={applyTemplate}
            />
          </div>

          <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_300px]">
            <div className="space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">
                  Recipient group <span className="text-red-600">*</span>
                </span>
                <Select
                  aria-label="Recipient group"
                  className="w-full"
                  placeholder="Select recipients"
                  value={recipientType}
                  onChange={setRecipientType}
                  options={[
                    { label: "Talent", value: "talent" },
                    { label: "Recruiters", value: "recruiter" },
                    { label: "Talent and Recruiters", value: "both" },
                  ]}
                />
              </label>

              <label className="block">
                <span className="mb-1.5 block text-sm font-medium text-slate-700">
                  Subject <span className="text-red-600">*</span>
                </span>
                <Input
                  maxLength={255}
                  placeholder="Add a clear subject"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                />
              </label>

              <div>
                <span className="mb-1.5 block text-sm font-medium text-slate-700">
                  Message <span className="text-red-600">*</span>
                </span>
                <div className="overflow-hidden rounded border border-slate-300 bg-white focus-within:border-teal-600 focus-within:ring-1 focus-within:ring-teal-600">
                  <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 p-2">
                    <Button
                      aria-label="Bold"
                      title="Bold"
                      icon={<Bold size={16} />}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => formatSelection("bold")}
                    />
                    <Button
                      aria-label="Italic"
                      title="Italic"
                      icon={<Italic size={16} />}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => formatSelection("italic")}
                    />
                    <Button
                      aria-label="Bulleted list"
                      title="Bulleted list"
                      icon={<List size={16} />}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => formatSelection("insertUnorderedList")}
                    />
                    <Button
                      aria-label="Numbered list"
                      title="Numbered list"
                      icon={<ListOrdered size={16} />}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => formatSelection("insertOrderedList")}
                    />
                    <Button
                      aria-label="Add hyperlink"
                      title="Add hyperlink"
                      icon={<Link2 size={16} />}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => setLinkOpen(true)}
                    />
                  </div>
                  <div
                    ref={editorRef}
                    aria-label="Message body"
                    aria-multiline="true"
                    contentEditable
                    role="textbox"
                    suppressContentEditableWarning
                    data-placeholder="Write your email message..."
                    className="min-h-52 p-4 text-sm leading-6 text-slate-800 outline-none [&:empty:before]:text-slate-400 [&:empty:before]:content-[attr(data-placeholder)] [&_a]:text-teal-700 [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-6"
                    onInput={(event) => setBody(event.currentTarget.innerHTML)}
                  />
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-2 pt-1">
                <Button
                  icon={<Eye size={16} />}
                  disabled={!subject.trim() || !body.trim()}
                  onClick={() => setPreviewOpen(true)}
                >
                  Preview
                </Button>
                <Button
                  type="primary"
                  icon={<Send size={16} />}
                  loading={sending}
                  disabled={
                    !recipientType ||
                    !subject.trim() ||
                    !body.trim() ||
                    countLoading ||
                    !recipientCount
                  }
                  onClick={confirmSend}
                >
                  Send email
                </Button>
              </div>
            </div>

            <aside className="border-l border-slate-200 pl-5 xl:min-h-64">
              <h3 className="text-sm font-semibold text-slate-900">Audience</h3>
              <p className="mt-1 text-sm text-slate-500">
                Only active users with valid email addresses are included.
              </p>
              <div className="mt-5 flex items-center gap-3">
                {countLoading ? (
                  <Spin size="small" />
                ) : (
                  <span className="text-3xl font-semibold tabular-nums text-slate-900">
                    {recipientCount?.toLocaleString() ?? "--"}
                  </span>
                )}
                <span className="text-sm text-slate-500">recipients</span>
              </div>
              {recipientType && (
                <p className="mt-2 text-xs text-slate-500">
                  {recipientLabels[recipientType]}
                </p>
              )}
              {recipientType && recipientCount === 0 && !countLoading && (
                <Alert
                  className="mt-4"
                  type="warning"
                  showIcon
                  message="No eligible recipients"
                />
              )}
            </aside>
          </div>
        </section>

        <section aria-labelledby="history-heading">
          <div className="mb-4 flex items-center justify-between gap-3">
            <div>
              <h2 id="history-heading" className="text-lg font-semibold text-slate-900">
                Email history
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Delivery progress and outcomes for emails sent from the Admin Portal.
              </p>
            </div>
            <Button
              aria-label="Refresh email history"
              title="Refresh email history"
              icon={<RefreshCw size={16} />}
              loading={historyLoading}
              onClick={() => void loadHistory()}
            />
          </div>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={history}
            loading={historyLoading}
            pagination={{
              current: historyPage,
              pageSize: 10,
              total: historyTotal,
              showSizeChanger: false,
              onChange: (page) => void loadHistory(page),
            }}
            locale={{ emptyText: <Empty description="No emails sent yet" /> }}
            scroll={{ x: 820 }}
          />
        </section>
      </div>

      <Modal
        title="Email preview"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewOpen(false)}>
            Close
          </Button>,
        ]}
      >
        <p className="mb-4 text-xs uppercase tracking-wide text-slate-500">
          To: {recipientType ? recipientLabels[recipientType] : "Select a recipient group"}
        </p>
        <h3 className="mb-4 text-lg font-semibold text-slate-900">{subject}</h3>
        <div
          className="prose prose-sm max-w-none text-slate-700 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body) }}
        />
      </Modal>

      <Modal
        title="Email details"
        open={Boolean(selectedEmail)}
        onCancel={() => setSelectedEmail(null)}
        footer={[
          <Button key="close" onClick={() => setSelectedEmail(null)}>
            Close
          </Button>,
        ]}
      >
        {selectedEmail && (
          <div className="space-y-4">
            <dl className="grid grid-cols-[110px_1fr] gap-y-2 text-sm">
              <dt className="text-slate-500">Subject</dt>
              <dd className="font-medium">{selectedEmail.subject}</dd>
              <dt className="text-slate-500">Recipients</dt>
              <dd>{recipientLabels[selectedEmail.recipient_type]}</dd>
              <dt className="text-slate-500">Recipient count</dt>
              <dd>{selectedEmail.recipient_count.toLocaleString()}</dd>
              <dt className="text-slate-500">Delivery</dt>
              <dd>
                <Tag color={statusColors[selectedEmail.status] ?? "default"}>
                  {selectedEmail.status}
                </Tag>
                {selectedEmail.sent_count} sent, {selectedEmail.failed_count} failed
              </dd>
              <dt className="text-slate-500">Sender</dt>
              <dd>{selectedEmail.sender}</dd>
              <dt className="text-slate-500">Date</dt>
              <dd>{new Date(selectedEmail.sent_at).toLocaleString()}</dd>
            </dl>
            {selectedEmail.delivery_statuses && (
              <div className="border-t border-slate-200 pt-3">
                <h3 className="mb-2 text-sm font-semibold">Delivery status breakdown</h3>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(selectedEmail.delivery_statuses).map(([status, count]) => (
                    <Tag key={status} color={statusColors[status] ?? "default"}>
                      {status}: {count}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
            {selectedEmail.body && (
              <div className="border-t border-slate-200 pt-3">
                <h3 className="mb-2 text-sm font-semibold">Message</h3>
                <div
                  className="prose prose-sm max-w-none text-slate-700"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(selectedEmail.body),
                  }}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title="Add hyperlink"
        open={linkOpen}
        onOk={addLink}
        onCancel={() => {
          setLinkOpen(false);
          setLinkUrl("");
        }}
        okText="Add link"
      >
        <Input
          autoFocus
          value={linkUrl}
          onChange={(event) => setLinkUrl(event.target.value)}
          onPressEnter={addLink}
          placeholder="https://example.com"
        />
      </Modal>
    </DashboardLayout>
  );
};